import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useJoyride, EVENTS, ACTIONS, STATUS } from "react-joyride";
import { ValidateLoginToken } from "../../Utility/Utility";
import getTranslation from "../../TranslationUtility/TranslationUtility";
import { callSiteTourAuditApi } from "../../Context/siteTourAuditApi";
import SiteTourTooltip from "./SiteTourTooltip";

// Audit status values — must match the backend ConstantSiteTourStatus (fixed contract, not translated).
const STATUS_COMPLETED = "Completed";
const STATUS_SKIPPED = "Skipped";
const STATUS_ANCHOR_MISSING = "Skipped-AnchorMissing";

// There used to be a pre-flight poll here that waited for every eligible step's anchor to exist
// in the DOM — checking all of them up front, before the tour was allowed to start — with a
// shared timeout across the whole queue. That meant one slow-to-mount target (e.g. a section
// waiting on its own async data fetch, unrelated to the tour) held up every other step too, since
// none of them could start until the whole batch was resolved one way or the other.
//
// react-joyride already does the right version of this natively, per step: when a step becomes
// current, it polls for that step's target on its own (see options.targetWaitTimeout below), and
// if the target never shows up it fires EVENTS.TARGET_NOT_FOUND and auto-advances to the next
// step — a step's target isn't checked until its own turn, and a slow one only delays itself, not
// steps before or after it. We now rely on that instead of re-implementing it here — see
// handleEvent's EVENTS.TARGET_NOT_FOUND branch, and the start effect below.
//
// Separate from all of that: a settle delay before the tour starts *at all*, once eligible — not
// waiting on any particular anchor, just giving the page a moment (styles, images, layout) before
// the overlay and first spotlight appear. That delay is the tourStartDelayMs prop, not a constant
// here — see the isPostLoginContentReady-style comment on the prop's default below, and the start
// effect further down for how it's used.
// Configuration for the per-step wait described above: once the tour has started and a step's
// turn comes, how long react-joyride waits for *that* step's own target to exist before giving up
// on it specifically and auto-advancing to the next one (options.targetWaitTimeout). Raise this if
// legitimately slow-mounting sections are getting skipped too eagerly; lower it if a genuinely
// missing anchor holds up the tour longer than it should.
const ANCHOR_WAIT_TIMEOUT_MS = 2000;
// Stacking layer for the pre-start overlay rendered while we wait out tourStartDelayMs — see
// isAwaitingStart. react-joyride's own overlay (options.zIndex below) sits one layer above this,
// so the moment it mounts it's already on top rather than tied with (and at the mercy of DOM
// order against) the pre-start overlay it's replacing.
const PRE_START_OVERLAY_ZINDEX = 10000;
const TOUR_ZINDEX = PRE_START_OVERLAY_ZINDEX + 1;

// Spotlight padding (px each side). react-joyride draws the cutout as a rounded rect whose
// corner radius is min(spotlightRadius, width/2, height/2), so we translate the target's own
// CSS border-radius into that numeric spotlightRadius to make the cutout follow its shape.
const SPOTLIGHT_PADDING = 0;
// The header section gets a bit more breathing room around its spotlight than other targets.
const HEADER_SPOTLIGHT_PADDING = 6;
// Thickness of the optional highlight ring drawn around the spotlight (see --site-tour-highlight).
const SPOTLIGHT_HIGHLIGHT_WIDTH = 2;
// Empty space between the spotlight cutout and the tooltip, before the arrow itself — react-joyride
// defaults this to 10px. The arrow's own length (ARROW_WEB.size / ARROW_MOBILE.size) sits on top of
// this, unaffected; lowering this only tightens the gap the arrow points across.
const TOOLTIP_OFFSET = 2;
// Every step scrolls its target this far below the viewport top (see scrollStepIntoView, which
// replaces react-joyride's own scroll). Deliberately small: the tooltip always sits *below* the
// target, so the useful space is what's left underneath it.
const SCROLL_TOP_OFFSET = 15;

// Pinned on every step rather than using placement "auto".
//
// react-joyride resolves the placement *before* it scrolls and never re-evaluates afterwards
// (tooltipFloating is built without whileElementsMounted: autoUpdate), so any viewport-derived
// choice is made against a layout that is about to change. Its adjustForPlacement() is meant to
// compensate the scroll for a tooltip drawn above the target, but it matches on the step's
// configured placement string, so "auto" never triggers it. The result was a tooltip placed above
// a target that then scrolled to the top of the viewport — i.e. off-screen.
//
// Declaring the side up front removes the guesswork entirely: the tooltip is always below the
// target, and our scroll puts the target near the top so there is room for it. flipOptions:false
// stops Floating UI flipping back to "top" on its (equally pre-scroll) view of available space.
const PINNED_BELOW_TARGET = {
  placement: "bottom",
  floatingOptions: { flipOptions: false },
};

// Backdrop opacity. Applied to the overlay element rather than baked into --site-tour-overlay as an
// alpha, so a theme colour picked as a plain hex still dims the page instead of blacking it out.
// Because of that, --site-tour-overlay should be an opaque hue — an alpha there compounds with this.
const OVERLAY_OPACITY = 0.5;
// react-joyride floors the spotlight's top edge (getElementPosition) but rounds its height
// (computeRect). So whenever a target's position lands on a fractional pixel the cutout starts
// slightly above the element and ends slightly above its bottom — a white band along the top of
// the highlight, with the bottom edge biting into the content.
//
// Moving one pixel from the top padding onto the bottom shifts the cutout down without resizing it
// (round(h + top + bottom) is unchanged), which effectively rounds the top edge to the nearer pixel
// instead of always flooring it. Only worth doing when the fraction is >= 0.5 — below that, floor
// is already the nearer of the two.
//
// Measured as a document-absolute top rather than rect.top: that is the value joyride actually
// floors, and unlike the viewport-relative one it doesn't shift when the page scrolls between
// measuring here and showing the step.
// On a replaced element the CSS padding is blank: the picture paints only inside the content box,
// while getBoundingClientRect() — and so the spotlight — covers the border box. A menu tile with
// `padding: 1px` therefore shows a hairline of page background inside the cutout on every side.
//
// Deliberately limited to img/video. On an ordinary element padding is painted (backgrounds and
// borders extend through it), so shrinking the cutout there would clip the element's own visuals.
const REPLACED_TAGS = new Set(["IMG", "VIDEO"]);
const blankPadding = (el) => {
  const zero = { top: 0, right: 0, bottom: 0, left: 0 };
  if (!el || !REPLACED_TAGS.has(el.tagName)) return zero;
  const cs = window.getComputedStyle(el);
  return {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };
};

// Solve for the spotlight padding that lands the cutout exactly on the target's painted box.
//
// react-joyride derives the cutout with Math.floor on the top edge and Math.round on the rest
// (getElementPosition / computeRect), so a target sitting on a fractional pixel always carries
// sub-pixel error. Which way that error goes matters: erring outward exposes page background as a
// visible hairline, while erring inward merely covers a fraction of the target's own pixels and
// cannot be seen. So rather than rounding to the nearest pixel, snap to ceil(top)/floor(bottom) of
// the painted box and let the cutout bite inward.
//
// Each padding is derived so joyride's own rounding is a no-op — the value it rounds is already an
// integer — which is why these come out fractional.
const snappedSpotlightPadding = (el, base) => {
  if (!el) return undefined;
  const rect = el.getBoundingClientRect();
  const blank = blankPadding(el);
  const documentTop = rect.top + window.scrollY;

  // The painted box, grown by whatever padding this step asked for.
  const top = documentTop + blank.top - base;
  const bottom = documentTop + rect.height - blank.bottom + base;
  const left = rect.left + blank.left - base;
  const right = rect.left + rect.width - blank.right + base;

  const cutoutTop = Math.ceil(top);
  const cutoutLeft = Math.ceil(left);

  // The top edge is the one joyride derives with Math.floor; every other edge goes through
  // Math.round. Round absorbs the ~1e-15 noise of floating-point arithmetic, floor does not — a
  // value meant to be exactly N can arrive as N - 1e-15 and drop a whole pixel, reopening the very
  // hairline this function exists to close. Aiming half a pixel into the row floors to the same N
  // whichever way the noise falls, and costs nothing: the resulting integer is unchanged.
  const paddingTop = documentTop - cutoutTop - 0.5;
  const paddingLeft = rect.left - cutoutLeft;
  return {
    top: paddingTop,
    left: paddingLeft,
    bottom: Math.floor(bottom) - cutoutTop - rect.height - paddingTop,
    right: Math.floor(right) - cutoutLeft - rect.width - paddingLeft,
  };
};

// `padding` is the spotlight padding actually in force for this target, so the cutout's corners
// follow the element's own rounding as it grows or shrinks away from the border box.
const spotlightRadiusForElement = (el, padding = SPOTLIGHT_PADDING) => {
  if (!el) return undefined;
  const raw = window.getComputedStyle(el).borderRadius;
  if (!raw) return undefined;
  const first = raw.split(" ")[0].trim(); // first corner is enough; designs use uniform radii
  if (first.endsWith("%")) {
    const pct = parseFloat(first);
    if (Number.isNaN(pct) || pct <= 0) return undefined;
    if (pct >= 50) return 9999; // fully rounded -> clamps to a circle/stadium
    const rect = el.getBoundingClientRect();
    return (pct / 100) * Math.min(rect.width, rect.height) + padding;
  }
  const px = parseFloat(first);
  return px > 0 ? px + padding : undefined;
};

// Arrow ("polygon" in the Figma) — the px box the triangle is inscribed in. react-joyride calls
// the width `arrowBase` and the height/protrusion `arrowSize`. `tip` is how far the apex is
// rounded off, so it reads as a soft point rather than a needle.
const ARROW_WEB = { base: 16, size: 13, tip: 3 };
const ARROW_MOBILE = { base: 13, size: 11, tip: 2 };
// Minimum gap the arrow keeps from the tooltip's corners. Raise it to pull the arrow further in
// from the edges, lower it to let the arrow sit closer to a corner.
const ARROW_SPACING = 12;
// react-joyride butts the arrow flush against the tooltip edge, so the two paint boundaries land
// on the same pixel and leave a hairline seam along the polygon's base. Bleeding the base into
// the card by 1px makes the triangle and the card render as one shape, with no bottom edge.
const ARROW_BLEED = 1;

// How far the right-pinned arrow sits in from the tooltip's right edge.
const ARROW_RIGHT_INSET = 40;
// On mobile a section tooltip spans nearly the whole viewport, and so does the section it points
// at — so Floating UI centring the arrow on the target says nothing useful. This pins it to the
// right instead. react-joyride spreads step.floatingOptions.middleware *after* its own `arrow`
// middleware, and Floating UI merges middleware data by name, so reusing the name "arrow" lets
// this override just the x it computed. Only x is touched; y and centerOffset are left alone.
const pinArrowRight = (arrowWidth) => ({
  name: "arrow",
  fn: ({ rects }) => ({
    data: {
      x: Math.max(0, rects.floating.width - arrowWidth - ARROW_RIGHT_INSET),
    },
  }),
});

// Triangle p1 -> apex -> p2 with the apex corner rounded to `radius`. The arc's tangent points sit
// radius / tan(halfAngle) back along each edge, which is what lets the curve meet the straight
// edges smoothly instead of kinking where they join.
const roundedTrianglePath = ([x1, y1], [ax, ay], [x2, y2], radius) => {
  const v1 = [x1 - ax, y1 - ay];
  const v2 = [x2 - ax, y2 - ay];
  const len1 = Math.hypot(v1[0], v1[1]);
  const len2 = Math.hypot(v2[0], v2[1]);
  const angle = Math.acos((v1[0] * v2[0] + v1[1] * v2[1]) / (len1 * len2));
  const back = radius / Math.tan(angle / 2);
  // Never let a tangent point pass the midpoint of its edge, or the path folds back on itself.
  const t1 = Math.min(back / len1, 0.5);
  const t2 = Math.min(back / len2, 0.5);
  const a1 = [ax + v1[0] * t1, ay + v1[1] * t1];
  const a2 = [ax + v2[0] * t2, ay + v2[1] * t2];
  // Which way the arc bends depends on the winding of the two edges around the apex.
  const sweep = v1[0] * v2[1] - v1[1] * v2[0] > 0 ? 0 : 1;
  return `M${x1},${y1} L${a1[0]},${a1[1]} A${radius},${radius} 0 0 ${sweep} ${a2[0]},${a2[1]} L${x2},${y2} Z`;
};

// Custom arrow so the polygon matches the Figma geometry, keeps a softened tip, and carries the
// bleed above. react-joyride passes { base, placement, size }; the fill comes from
// options.arrowColor, which it applies as `color` on the wrapper span — hence `currentColor`.
const SiteTourArrow = ({ base, placement, size, tipRadius = ARROW_WEB.tip }) => {
  const [side] = String(placement || "bottom").split("-");
  const isVertical = side === "top" || side === "bottom";
  const bled = size + ARROW_BLEED;
  const half = base / 2;
  const baseStyle = { display: "block", overflow: "visible" };

  let corners;
  let style = baseStyle;
  switch (side) {
    case "top": // arrow hangs below the tooltip, apex pointing down
      corners = [[0, 0], [half, size], [base, 0]];
      style = { ...baseStyle, marginTop: -ARROW_BLEED };
      break;
    case "left": // arrow sits off the tooltip's right edge, apex pointing right
      corners = [[0, 0], [size, half], [0, base]];
      style = { ...baseStyle, marginLeft: -ARROW_BLEED };
      break;
    case "right": // arrow sits off the tooltip's left edge, apex pointing left
      corners = [[bled, 0], [0, half], [bled, base]];
      break;
    default: // "bottom" — arrow sits above the tooltip, apex pointing up
      corners = [[0, bled], [half, 0], [base, bled]];
  }

  return (
    <svg
      width={isVertical ? base : bled}
      height={isVertical ? bled : base}
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path fill="currentColor" d={roundedTrianglePath(...corners, tipRadius)} />
    </svg>
  );
};

// Once a tour has ended in a session we don't auto-trigger it again until the next login
// (the backend recomputes eligibility on the next PostLoginDetails).
const SESSION_DONE_KEY = "siteTourSessionDone";

// Stable key for a (section, content?) tour item, used to dedupe Completed vs. Skipped audits.
const itemKey = (data) => `${data?.sectionId}:${data?.sectionContentId ?? ""}`;
const auditItem = (data) => ({
  sectionId: data?.sectionId,
  sectionContentId: data?.sectionContentId ?? null,
});

// SectionMaster.deviceType: true = mobile-only, false = web-only, null/undefined = both (same
// convention the admin screens use for the Mobile/Web/Both label). A section restricted to the
// other viewport can never mount here — that's not react-joyride racing a slow-loading anchor,
// it's known for certain the moment eligibleSteps is built, so there's nothing worth spending
// ANCHOR_WAIT_TIMEOUT_MS waiting on. See eligibleSteps' deviceMismatched below.
const isDeviceMismatch = (deviceType, isMobileView) =>
  (deviceType === true && !isMobileView) || (deviceType === false && isMobileView);

// Both isPostLoginContentReady and tourStartDelayMs default closed deliberately: a caller that
// forgets to pass them must leave the tour never starting, rather than starting before the page
// it targets has actually rendered — see the isPostLoginContentReady comment in HomePage.js.
// tourStartDelayMs specifically: null means "the switch-account response this login's tour is
// waiting on hasn't settled yet" (see the same file), not "use some default delay" — once it
// settles, HomePage.js hands over the actual number of ms to wait, sized off how long that
// response took. See shouldPrepare and the start effect below for how it's consumed.
const SiteTourManager = ({ isMobileView = false, isPostLoginContentReady = false, tourStartDelayMs = null }) => {
  const homePageData = useSelector(
    (state) => state.homePageSliceReducer?.homePageData
  );
  const token = ValidateLoginToken();
  const memberIdentifier = token?.memberIdentifier;
  const isLoggedIn = sessionStorage.getItem("login");
  const translation = getTranslation();

  // Nested queue: [{ sectionId, sectionTour, contents: [{ sectionContentId }] }]
  const siteTour = homePageData?.siteTour || [];
  const sectionMasters = homePageData?.sectionMasters || [];
  const sectionContentMasters = homePageData?.sectionContentMasters || [];

  // Items already recorded this run — Completed via STEP_AFTER, or Skipped-AnchorMissing via
  // react-joyride's own TARGET_NOT_FOUND — so a later Skip only marks whatever's left over.
  const completedRef = useRef(new Set());
  const missingRef = useRef(new Set());

  // All backend-eligible steps, flattened in list order and interleaved: for each section
  // entry top-to-bottom emit the section step (if it has its own tour) then its content
  // steps in order, before moving to the next section. (The per-session step cap is applied
  // server-side, from Api_Config — see GetSiteTourStepLimit in HomePageProvider.)
  // deviceMismatched: items whose section is ruled out on this viewport by SectionMaster.deviceType
  // — excluded from steps entirely (see isDeviceMismatch above) and audited as Skipped-AnchorMissing
  // straight away, in the start effect below, instead of being handed to react-joyride to discover
  // the same thing the slow way. A section that won't mount here takes everything nested under it
  // down with it, so its content items are routed to deviceMismatched too — on the section's
  // deviceType, not their own, since Section_Content_Master carries no such field at all.
  const { steps: eligibleSteps, deviceMismatched } = useMemo(() => {
    const steps = [];
    const deviceMismatched = [];
    (siteTour || []).forEach((entry) => {
      if (!entry) return;

      const section = sectionMasters.find((s) => s?.id === entry.sectionId);
      const sectionMismatched = isDeviceMismatch(section?.deviceType, isMobileView);

      if (entry.sectionTour) {
        const content = section?.siteTourDetails?.content;
        if (content) {
          if (sectionMismatched) {
            deviceMismatched.push({ sectionId: entry.sectionId, sectionContentId: null });
          } else {
            const isHeader = section?.sectionType === translation?.homePageHeader;
            steps.push({
              target: `#tour_sec_${entry.sectionId}`,
              content,
              data: { sectionId: entry.sectionId, sectionContentId: null, isHeader },
              ...PINNED_BELOW_TARGET,
              ...(isHeader ? { spotlightPadding: HEADER_SPOTLIGHT_PADDING } : {}),
              // Mobile section tooltips additionally point from the right rather than at the centre
              // of a full-width section. Merged into the pinned floatingOptions above.
              ...(isMobileView
                ? {
                  floatingOptions: {
                    ...PINNED_BELOW_TARGET.floatingOptions,
                    middleware: [pinArrowRight(ARROW_MOBILE.base)],
                  },
                }
                : {}),
            });
          }
        }
      }

      (entry.contents || []).forEach((c) => {
        if (!c) return;
        const item = sectionContentMasters.find(
          (sc) => sc?.id === c.sectionContentId
        );
        const content = item?.siteTourDetails?.content;
        if (!content) return;

        if (sectionMismatched) {
          deviceMismatched.push({ sectionId: entry.sectionId, sectionContentId: c.sectionContentId });
          return;
        }

        steps.push({
          target: `#tour_content_${c.sectionContentId}`,
          content,
          data: {
            sectionId: entry.sectionId,
            sectionContentId: c.sectionContentId,
          },
          ...PINNED_BELOW_TARGET,
        });
      });
    });
    return { steps, deviceMismatched };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(siteTour), sectionMasters, sectionContentMasters, isMobileView]);

  const writeAudit = useCallback(
    (items, status) => {
      const valid = (items || []).filter((it) => it && it.sectionId);
      if (!valid.length || !memberIdentifier) return;
      callSiteTourAuditApi({ memberIdentifier, items: valid, status }).catch(
        () => { }
      );
    },
    [memberIdentifier]
  );

  // Steps handed to react-joyride — seeded once from eligibleSteps when the tour starts (see the
  // start effect below), not filtered by anchor presence: each step's target is checked by
  // react-joyride itself when that step's turn actually comes (options.targetWaitTimeout), not
  // up front. Per-step spotlight geometry gets patched in here too, in handleEvent's STEP_BEFORE
  // branch, once each target is confirmed to exist — see that branch for why it can't be done any
  // earlier than that.
  const [tourSteps, setTourSteps] = useState([]);

  // We own the scrolling, not react-joyride (see options.skipScroll).
  //
  // Its getScrollTo() returns 0 both when the target is missing and when the target sits closer to
  // the document top than scrollOffset — and 0 means "top of the page". So a step whose anchor has
  // gone (lazy section remounted, a conditional branch flipped) silently yanks the whole page up,
  // which is indistinguishable from a legitimate scroll-to-top. Doing it here lets us just not
  // scroll when the anchor is absent, and keeps the offsets in one readable place.
  //
  // Safe to do ourselves because the floater is positioned in document coordinates (Floating UI
  // strategy "absolute"), so the tooltip stays glued to its target across the scroll.
  const scrollStepIntoView = useCallback(
    (step) => {
      const el = step?.target ? document.querySelector(step.target) : null;
      if (!el) return;

      const absoluteTop = el.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, absoluteTop - SCROLL_TOP_OFFSET),
        behavior: "smooth",
      });
    },
    []
  );

  const handleEvent = useCallback(
    (data) => {
      const { type, action, status, step } = data || {};
      const stepData = step?.data;

      if (type === EVENTS.STEP_BEFORE) {
        // react-joyride only reaches STEP_BEFORE once it has confirmed step.target exists (see
        // the constants-block comment above) — so this is the earliest point at which measuring
        // the element is both possible and not wasted work on a step that might get skipped.
        // Measuring any earlier (e.g. for every eligible step up front) would mean measuring
        // elements that either aren't mounted yet or never end up shown at all.
        const el = step?.target ? document.querySelector(step.target) : null;
        if (el) {
          const base = stepData?.isHeader ? HEADER_SPOTLIGHT_PADDING : SPOTLIGHT_PADDING;
          const padding = snappedSpotlightPadding(el, base);
          const spotlightRadius = spotlightRadiusForElement(
            el,
            padding ? padding.left : base
          );
          if (padding || spotlightRadius != null) {
            // Patches just this one step's geometry into tourSteps. Safe to update mid-tour:
            // react-joyride reads steps[index] fresh each render (useMemo, not an effect keyed
            // on the array's identity), so a new array reference doesn't reset its progress.
            setTourSteps((prev) =>
              prev.map((s) =>
                s.target === step.target
                  ? {
                    ...s,
                    ...(padding ? { spotlightPadding: padding } : {}),
                    ...(spotlightRadius != null ? { spotlightRadius } : {}),
                  }
                  : s
              )
            );
          }
        }
        scrollStepIntoView(step);
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        // react-joyride already waited out this step's own options.targetWaitTimeout and is
        // about to auto-advance to the next step on its own — nothing to do here but audit it.
        if (stepData) {
          missingRef.current.add(itemKey(stepData));
          writeAudit([auditItem(stepData)], STATUS_ANCHOR_MISSING);
        }
        return;
      }

      if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT && stepData) {
        completedRef.current.add(itemKey(stepData));
        writeAudit([auditItem(stepData)], STATUS_COMPLETED);
        return;
      }

      if (type === EVENTS.TOUR_END || type === EVENTS.TOUR_STATUS) {
        if (status === STATUS.SKIPPED) {
          // Exclude anything already resolved either way this run — completed normally, or
          // already recorded anchor-missing by react-joyride's own per-step check — so Skip only
          // writes for steps that genuinely never got a turn yet.
          const remaining = tourSteps
            .filter((s) => !completedRef.current.has(itemKey(s.data)) && !missingRef.current.has(itemKey(s.data)))
            .map((s) => auditItem(s.data));
          writeAudit(remaining, STATUS_SKIPPED);
        }
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
          sessionStorage.setItem(SESSION_DONE_KEY, "1");
        }
      }
    },
    [tourSteps, writeAudit, scrollStepIntoView]
  );

  const TooltipComponent = useCallback(
    (props) => <SiteTourTooltip {...props} isMobileView={isMobileView} translation={translation} />,
    [isMobileView, translation]
  );

  const ArrowComponent = useCallback(
    (props) => (
      <SiteTourArrow
        {...props}
        tipRadius={isMobileView ? ARROW_MOBILE.tip : ARROW_WEB.tip}
      />
    ),
    [isMobileView]
  );

  const alreadyDoneThisSession = sessionStorage.getItem(SESSION_DONE_KEY) === "1";
  const shouldPrepare =
    Boolean(isLoggedIn) &&
    Boolean(memberIdentifier) &&
    eligibleSteps.length > 0 &&
    !alreadyDoneThisSession &&
    // homePageData.siteTour arriving (which is all eligibleSteps needs) doesn't mean the page has
    // finished showing its own loading screen — HomePage.js renders the real sections (this tour's
    // anchors) only once its own loader/personalization gate clears, on a schedule PostLoginDetails
    // knows nothing about. Without this, the tour could become "ready" and start while the user is
    // still looking at CustomLoader3, with none of its targets mounted yet.
    isPostLoginContentReady &&
    // Also gates on HomePage.js's switch-account response, not just its loading screen — see
    // tourStartDelayMs above. Null means that response (success or failure, doesn't matter) hasn't
    // settled yet: fail closed on it rather than starting on some assumed delay while it might
    // still be in flight on a slow network.
    tourStartDelayMs != null;

  const { Tour, controls } = useJoyride({
    steps: tourSteps,
    // debug: true, // TEMP: logs react-joyride lifecycle/actions to the console
    continuous: true,
    scrollToFirstStep: true,
    onEvent: handleEvent,
    tooltipComponent: TooltipComponent,
    locale: {
      last: translation?.getStarted,
      next: translation?.next,
      back: translation?.previous,
      skip: translation?.skipTour,
    },
    arrowComponent: ArrowComponent,
    // Explicit, responsive floater width so it doesn't shrink-to-fit to a sliver near an edge.
    styles: {
      floater: {
        width: isMobileView ? (parseInt("calc(100vw - 24px)", 10) > 335 ? "calc(100vw - 24px)" : 335) : 416,
        maxWidth: "94vw",
      },
      // Ring around the highlighted target. react-joyride renders this as an SVG <path> with
      // fill:none and spreads these keys as element props, so the ring has to be a stroke — and it
      // has to go inside `style`, because `var()` resolves in CSS but not in an SVG attribute.
      // Replacing `style` means re-declaring pointerEvents, which joyride sets there by default.
      // Transparent default = no ring unless a client configures one.
      spotlight: {
        // Spread onto the <path> as props, so this className reaches it and _siteTour.scss can
        // hang the highlight-pop animation off it.
        className: "site-tour-spotlight-ring",
        style: {
          pointerEvents: "none",
          stroke: "var(--site-tour-highlight, transparent)",
          strokeWidth: SPOTLIGHT_HIGHLIGHT_WIDTH,
        },
      },
      overlay: { opacity: OVERLAY_OPACITY },
    },
    options: {
      overlayClickAction: false, // clicking the backdrop never dismisses the tour
      blockTargetInteraction: true, // highlighted target visible but non-interactable
      dismissKeyAction: false, // disable built-in Esc; mapped to Skip below
      skipBeacon: true, // show the tooltip immediately, no beacon
      showProgress: false, // we render our own "Step X of Y"
      spotlightPadding: SPOTLIGHT_PADDING,
      offset: TOOLTIP_OFFSET,
      skipScroll: true, // scrolling is handled by scrollStepIntoView above
      zIndex: TOUR_ZINDEX,
      // Hue only — the 60% comes from styles.overlay above.
      overlayColor: "var(--site-tour-overlay, #000000)",
      // See ANCHOR_WAIT_TIMEOUT_MS above: the operative per-step anchor budget, independent per
      // step, so a slow one only costs itself, not the steps before or after it.
      targetWaitTimeout: ANCHOR_WAIT_TIMEOUT_MS,
      // Arrow geometry + placement. arrowColor becomes a CSS `color` that the polygon picks up via
      // fill="currentColor", so it must be a solid colour — the card's --site-tour-bg is a gradient
      // and would be invalid here. --site-tour-arrow tracks it separately.
      arrowColor: "var(--site-tour-arrow, #DEE0F6)",
      arrowBase: isMobileView ? ARROW_MOBILE.base : ARROW_WEB.base,
      arrowSize: isMobileView ? ARROW_MOBILE.size : ARROW_WEB.size,
      arrowSpacing: ARROW_SPACING,
    },
  });

  // Seed tourSteps from eligibleSteps and start after a settle delay once we're allowed to — no
  // anchor check here at all. Each step's own target existing (or not) is react-joyride's problem
  // now, resolved on that step's own turn via options.targetWaitTimeout, not something we gate the
  // whole tour's start on. The delay itself is tourStartDelayMs, sized in HomePage.js off how long
  // this login's switch-account response took — shouldPrepare above already guarantees it's a
  // number by the time this effect gets past its early return, so no fallback is needed here.
  //
  // startedRef only flips inside the timeout callback, once it actually fires — not as soon as
  // the effect runs. That matters: if shouldPrepare/eligibleSteps change again while the timer is
  // still pending (e.g. eligibleSteps recomputing for an unrelated reason before the delay is up),
  // the cleanup below clears that timer and this effect reschedules a fresh one against the latest
  // values — so the delay is measured from the last settle, not the first flicker of eligibility.
  // Once the timer actually fires and startedRef flips true, later reruns hit the early-return and
  // leave an in-flight tour's steps alone.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!shouldPrepare || startedRef.current) return undefined;

    const timer = setTimeout(() => {
      startedRef.current = true;
      // Known-missing on this viewport before react-joyride ever sees them — see
      // isDeviceMismatch/deviceMismatched above. Audited here rather than left for
      // EVENTS.TARGET_NOT_FOUND, since they were never included in tourSteps for
      // react-joyride to reach a turn on in the first place.
      if (deviceMismatched.length) {
        deviceMismatched.forEach((item) => missingRef.current.add(itemKey(item)));
        writeAudit(deviceMismatched, STATUS_ANCHOR_MISSING);
      }
      setTourSteps(eligibleSteps);
      controls.start();
    }, tourStartDelayMs);

    return () => clearTimeout(timer);
  }, [shouldPrepare, eligibleSteps, deviceMismatched, controls, writeAudit, tourStartDelayMs]);

  // True for exactly the tourStartDelayMs window above: eligible to start, but the timer hasn't
  // fired yet. startedRef flips inside that timeout callback, in the same tick as setTourSteps —
  // which re-renders this component — so reading it here always reflects the latest state; no
  // separate piece of state is needed just to react to the flip.
  const isAwaitingStart = shouldPrepare && !startedRef.current;

  // Without this, a click landing in that window reaches whatever's underneath (a card, a link)
  // before react-joyride's own overlay exists to intercept it — the page would briefly behave as
  // if no tour were about to start at all. Transparent and otherwise inert: it exists purely to
  // eat clicks until the real overlay takes over.
  const preStartOverlay = isAwaitingStart ? (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: PRE_START_OVERLAY_ZINDEX,
        background: "transparent",
        pointerEvents: "auto",
      }}
    />
  ) : null;

  // Escape key = Skip (dismiss the whole tour permanently).
  useEffect(() => {
    if (tourSteps.length === 0) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        controls?.skip?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [tourSteps, controls]);

  return (
    <>
      {preStartOverlay}
      {Tour}
    </>
  );
};

export default SiteTourManager;
